import { useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';

const TOP_OFFSET = 0; // fully expanded
const INITIAL_OFFSET = 136; // gap from top when collapsed
const BOTTOM_PADDING = 48; // space under the content when fully expanded

export default function WalletBottomSheet({
  children,
  contentRef,
}: {
  children: React.ReactNode;
  contentRef: React.RefObject<HTMLDivElement>;
}) {
  const [translateY, setTranslateY] = useState(INITIAL_OFFSET);
  const [paddingBottom, setPaddingBottom] = useState(BOTTOM_PADDING);
  const innerRef = useRef<HTMLDivElement>(null);

  // The sheet starts shifted down, so its lower part is off-screen and short content
  // needs extra bottom padding to be scrollable into view. The padding is derived only
  // from the content and container sizes (never from the scroll position), otherwise
  // scrolling would change the scroll range and the sheet would bounce.
  useEffect(() => {
    const container = contentRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;

    const updatePadding = () => {
      const needed =
        (container.clientHeight -
          inner.offsetHeight +
          INITIAL_OFFSET +
          BOTTOM_PADDING) /
        2;
      setPaddingBottom(Math.max(BOTTOM_PADDING, Math.ceil(needed)));
    };

    const observer = new ResizeObserver(updatePadding);
    observer.observe(container);
    observer.observe(inner);
    updatePadding();

    return () => observer.disconnect();
  }, [contentRef]);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;

    const maxOffset = INITIAL_OFFSET;

    let next = maxOffset - scrollTop;

    // clamp
    next = Math.max(TOP_OFFSET, next);
    next = Math.min(maxOffset, next);

    setTranslateY(next);
  };

  return (
    <div
      className={css({
        position: 'fixed',
        inset: 0,
        maxWidth: 'maxContainer',
        mx: 'auto',
        pointerEvents: 'none',
        zIndex: 2,
      })}
    >
      <div
        style={{
          transform: `translateY(${translateY}px)`,
        }}
        className={css({
          bg: 'white',
          height: 'calc(100% - 1px)',
          borderTopRadius: '24px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -16px 12px rgba(0,0,0,0.04)',
          overflow: 'hidden',
          pointerEvents: 'auto',
          transition: 'transform 0.1s linear',
          willChange: 'transform',
        })}
      >
        <div
          className={css({
            pt: 16,
            pb: 16,
            display: 'flex',
            justifyContent: 'center',
          })}
        >
          <div
            className={css({
              width: '32px',
              height: '4px',
              bg: '#D1D1D1',
              borderRadius: 50,
            })}
          />
        </div>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          style={{ paddingBottom }}
          className={css({
            scrollbarWidth: 'none',
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            px: 16,
          })}
        >
          <div ref={innerRef}>{children}</div>
        </div>
      </div>
    </div>
  );
}
